import cloneDeep from 'lodash.clonedeep';
import { FilterType } from 'Js/enums/filterType.js';
import { camelCaseToKebabCase } from 'Js/utils/stringUtils';
import Filter from '../types/filter';
import { FilterEvent } from '../types/filterEvent';
export function useFilter(props, { emit }, modelValue, updateDisplayString) {
    // Destructure props
    const { 
    /**
     * {FilterType} that is bound to the filter select box for single filter buttons.
     * @values equal, greater, smaller, contains, startsWith, greaterEqual, smallerEqual
     */
    type, 
    /**
     * {FilterType} that is applied when the filter is reset.
     * @values equal, greater, smaller, contains, startsWith, greaterEqual, smallerEqual
     */
    initialType, 
    /**
     * String that is displayed as the hint in the text input for single filter buttons.
     */
    hint, 
    /**
     * String that is displayed as the label in the text input for single filter buttons.
     */
    label, 
    /**
     * Specifies whether the @FilterType can be change (true) or not (false).
     */
    filterTypeSelectionVisible, 
    /**
     * String that is displayed as the text for the apply button.
     */
    applyButtonText, 
    /**
     * String that is displayed as the text for the reset button.
     */
    resetButtonText, 
    /**
     * String that is displayed when no filter is selected.
     */
    noFilterText, } = toRefs(props);
    /**
     * String that is displayed in the button to indicate the selected value.
     */
    const displayString = ref(noFilterText?.value ?? t?.('common.dataGrid.selectAll'));
    /**
     * Specifies whether the filter menu is visible (true) or not (false).
     */
    const menuIsVisible = ref(false);
    const changedValue = ref(modelValue.value);
    const selectedType = ref(type?.value);
    const isSingleFilter = ref(true);
    /**
     * Filter options for number values.
     * Includes "=", "<", ">".
     */
    const typeItemsNumber = [
        { text: t?.('common.filters.='), value: FilterType.Equal },
        { text: t?.('common.filters.<'), value: FilterType.Smaller },
        { text: t?.('common.filters.>'), value: FilterType.Greater },
    ];
    /**
     * Filter options for string values.
     * Includes "contains", "=", "startsWith".
     */
    const typeItemsString = [
        { text: t?.('common.filters.contains'), value: FilterType.Contains },
        { text: t?.('common.filters.='), value: FilterType.Equal },
        { text: t?.('common.filters.startsWith'), value: FilterType.StartsWith },
    ];
    /**
     * Filter options for string values.
     * Includes "contains", "=", "startsWith".
     */
    const typeItemsWithLikeString = [
        { text: t?.('common.filters.contains'), value: FilterType.Contains },
        { text: t?.('common.filters.like'), value: FilterType.Like },
        { text: t?.('common.filters.='), value: FilterType.Equal },
        { text: t?.('common.filters.startsWith'), value: FilterType.StartsWith },
    ];
    // Computed properties
    const noFilterTextComputed = computed(() => noFilterText.value ?? t?.('common.dataGrid.selectAll'));
    const resetButtonTextComputed = computed(() => resetButtonText.value ?? t?.('common.filters.reset'));
    const applyButtonTextComputed = computed(() => applyButtonText.value ?? t?.('common.filters.apply'));
    const shouldShowPrefixComputed = computed(() => shouldShowPrefixMethod(props.type));
    // Watchers
    watch(modelValue, (value) => {
        if (isSingleFilter.value) {
            changedValue.value = value;
        }
        else {
            const tempValue = {};
            Object.keys(value)?.forEach((key) => {
                tempValue[key] = Filter.from(cloneDeep(value[key]));
            });
            changedValue.value = tempValue;
        }
        updateDisplayString?.(value); // Update the display string from the component
    }, { deep: true });
    watch(() => type, (value) => {
        selectedType.value = value;
        emit('update:type', value);
    });
    // Methods
    const returnTypeId = (fieldKey) => {
        if (fieldKey)
            return returnId(fieldKey) + '-filter-type';
    };
    function shouldShowPrefixMethod(localType) {
        return localType !== FilterType.Equal &&
            typeItemsNumber
                .map(type => type.value)
                .includes(localType);
    }
    const returnId = (fieldKey) => {
        if (fieldKey)
            return 'en-filter-input-' + camelCaseToKebabCase(fieldKey);
    };
    const returnFilterButtonId = (fieldKey) => {
        if (fieldKey)
            return 'en-filter-button-' + camelCaseToKebabCase(fieldKey);
    };
    const onResetButtonClicked = () => {
        if (isSingleFilter.value) {
            changedValue.value = undefined;
            selectedType.value = initialType.value;
        }
        else {
            Object.keys(changedValue.value).forEach((key) => changedValue.value[key].reset());
        }
        updateDisplayString(changedValue.value); // Update the display string from the component
        menuIsVisible.value = false;
        emitUpdate(changedValue.value);
    };
    const applyFilters = () => {
        updateDisplayString?.(changedValue.value); // Update the display string from the component
        menuIsVisible.value = false;
        emitUpdate(changedValue.value);
    };
    const getDisplayString = (value) => {
        const valueString = value || noFilterTextComputed.value;
        return valueString;
    };
    const emitUpdate = (value) => {
        /**
         * Triggers when the filter is updated.
         */
        emit(FilterEvent.Input, changedValue.value);
        /**
         * Triggers when the type is updated.
         */
        emit('update:type', selectedType.value);
        emit('update:modelValue', changedValue.value);
        emit(FilterEvent.FilterUpdated, value);
    };
    // Return only the necessary references, computed properties, and methods
    return {
        // Data
        displayString,
        menuIsVisible,
        changedValue,
        selectedType,
        isSingleFilter,
        // Computed
        noFilterTextComputed,
        resetButtonTextComputed,
        applyButtonTextComputed,
        shouldShowPrefixComputed,
        // Methods
        typeItemsNumber,
        shouldShowPrefixMethod,
        typeItemsString,
        typeItemsWithLikeString,
        returnTypeId,
        returnId,
        returnFilterButtonId,
        onResetButtonClicked,
        applyFilters,
        getDisplayString,
        emitUpdate,
    };
}
